import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getMemoryFiles, 
  getMemoryContent, 
  updateMemoryContent, 
  processBrainDump,
  type AspectTreeNode,
  type MemoryFile,
  type BrainDumpResult
} from '../lib/api';
import styles from './MemoryEditor.module.css';

interface MemoryEditorProps {
  aspect: AspectTreeNode;
}

export default function MemoryEditor({ aspect }: MemoryEditorProps) {
  const [activeFile, setActiveFile] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [showBrainDump, setShowBrainDump] = useState(false);
  const [brainDumpText, setBrainDumpText] = useState('');
  const [brainDumpResult, setBrainDumpResult] = useState<BrainDumpResult | null>(null);
  const [showNewFile, setShowNewFile] = useState(false);
  const [newFileName, setNewFileName] = useState('');
  
  const queryClient = useQueryClient();
  
  const { data: files } = useQuery({
    queryKey: ['memory-files', aspect.aspectId],
    queryFn: () => getMemoryFiles(aspect.aspectId),
  });
  
  const { data: content, isLoading: contentLoading } = useQuery({
    queryKey: ['memory-content', aspect.aspectId, activeFile],
    queryFn: () => getMemoryContent(aspect.aspectId, activeFile!),
    enabled: !!activeFile,
  });
  
  const saveMutation = useMutation({
    mutationFn: () => updateMemoryContent(aspect.aspectId, activeFile!, editContent),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memory-files', aspect.aspectId] });
      queryClient.invalidateQueries({ queryKey: ['memory-content', aspect.aspectId, activeFile] });
      setIsEditing(false);
    },
  });
  
  const brainDumpMutation = useMutation({
    mutationFn: () => processBrainDump(aspect.aspectId, brainDumpText),
    onSuccess: (result) => {
      setBrainDumpResult(result);
      queryClient.invalidateQueries({ queryKey: ['memory-files', aspect.aspectId] });
      queryClient.invalidateQueries({ queryKey: ['project-tree'] });
    },
  });
  
  const createFileMutation = useMutation({
    mutationFn: () => updateMemoryContent(aspect.aspectId, newFileName, '# ' + newFileName + '\n\n'),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['memory-files', aspect.aspectId] });
      setShowNewFile(false);
      setNewFileName('');
      setActiveFile(newFileName);
    },
  });
  
  const handleEdit = () => {
    if (content) {
      setEditContent(content.content);
      setIsEditing(true);
    }
  };
  
  const handleCancel = () => {
    setIsEditing(false);
    setEditContent('');
  };
  
  const defaultFiles: MemoryFile[] = [
    { filename: 'decisions.md', latestVersion: 0 },
    { filename: 'architecture.md', latestVersion: 0 },
    { filename: 'roadmap.md', latestVersion: 0 },
    { filename: 'notes.md', latestVersion: 0 },
  ];
  
  const displayFiles = files && files.length > 0 ? files : defaultFiles;
  
  return (
    <div className={styles.editor}>
      <div className={styles.toolbar}>
        <div className={styles.tabs}>
          {displayFiles.map((file) => (
            <button
              key={file.filename}
              className={`${styles.tab} ${activeFile === file.filename ? styles.active : ''}`}
              onClick={() => {
                setActiveFile(file.filename);
                setIsEditing(false);
              }}
            >
              {file.filename}
              {file.latestVersion > 0 && (
                <span className={styles.version}>v{file.latestVersion}</span>
              )}
            </button>
          ))}
          <button 
            className={styles.newTab}
            onClick={() => setShowNewFile(true)}
          >
            +
          </button>
        </div>
        
        <div className={styles.actions}>
          <button
            className={`btn btn-secondary ${styles.brainDumpBtn}`}
            onClick={() => setShowBrainDump(true)}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 5a3 3 0 1 0-5.997.125 4 4 0 0 0-2.526 5.77 4 4 0 0 0 .556 6.588A4 4 0 1 0 12 18Z"/>
              <path d="M12 5a3 3 0 1 1 5.997.125 4 4 0 0 1 2.526 5.77 4 4 0 0 1-.556 6.588A4 4 0 1 1 12 18Z"/>
            </svg>
            Brain Dump
          </button>
        </div>
      </div>
      
      {showNewFile && (
        <div className={styles.newFileForm}>
          <input
            type="text"
            className="input"
            placeholder="filename.md"
            value={newFileName}
            onChange={(e) => setNewFileName(e.target.value)}
            autoFocus
          />
          <button 
            className="btn btn-primary"
            onClick={() => createFileMutation.mutate()}
            disabled={!newFileName || createFileMutation.isPending}
          >
            Create
          </button>
          <button 
            className="btn btn-ghost"
            onClick={() => {
              setShowNewFile(false);
              setNewFileName('');
            }}
          >
            Cancel
          </button>
        </div>
      )}
      
      <div className={styles.content}>
        {!activeFile ? (
          <div className={styles.placeholder}>
            <p>Select a file to view or edit</p>
          </div>
        ) : contentLoading ? (
          <div className={styles.loading}>Loading...</div>
        ) : isEditing ? (
          <div className={styles.editMode}>
            <textarea
              className={styles.textarea}
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              autoFocus
            />
            <div className={styles.editActions}>
              <button className="btn btn-ghost" onClick={handleCancel}>
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          <div className={styles.viewMode}>
            <div className={styles.viewHeader}>
              <span className={styles.fileName}>{activeFile}</span>
              <button className="btn btn-secondary" onClick={handleEdit}>
                Edit
              </button>
            </div>
            <pre className={styles.preview}>
              {content?.content || '(Empty file)'}
            </pre>
          </div>
        )}
      </div>
      
      {showBrainDump && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Brain Dump</h3>
            <p className={styles.modalHint}>
              Paste your unstructured notes, ideas, or thoughts. AI will organize them into decisions, architecture notes, and actionable tickets.
            </p>
            
            {brainDumpResult ? (
              <div className={styles.brainDumpResult}>
                <h4>Results</h4>
                <p className={styles.summary}>{brainDumpResult.summary}</p>
                
                {brainDumpResult.updatedDocuments.length > 0 && (
                  <div className={styles.resultSection}>
                    <h5>Updated Documents</h5>
                    {brainDumpResult.updatedDocuments.map((doc) => (
                      <div key={doc.filename} className={styles.resultItem}>
                        <span className={styles.resultName}>{doc.filename}</span>
                        <span className={styles.resultDesc}>{doc.changesSummary}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                {brainDumpResult.proposedTickets.length > 0 && (
                  <div className={styles.resultSection}>
                    <h5>Proposed Tickets</h5>
                    {brainDumpResult.proposedTickets.map((ticket, i) => (
                      <div key={i} className={styles.resultItem}>
                        <span className={styles.resultName}>{ticket.title}</span>
                        <span className={`${styles.priority} ${styles[ticket.priority]}`}>
                          {ticket.priority}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className={styles.modalActions}>
                  <button 
                    className="btn btn-primary"
                    onClick={() => {
                      setShowBrainDump(false);
                      setBrainDumpResult(null);
                      setBrainDumpText('');
                    }}
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <textarea
                  className={styles.brainDumpInput}
                  placeholder="Paste your brain dump here..."
                  value={brainDumpText}
                  onChange={(e) => setBrainDumpText(e.target.value)}
                />
                
                <div className={styles.modalActions}>
                  <button 
                    className="btn btn-ghost"
                    onClick={() => {
                      setShowBrainDump(false);
                      setBrainDumpText('');
                    }}
                  >
                    Cancel
                  </button>
                  <button 
                    className="btn btn-primary"
                    onClick={() => brainDumpMutation.mutate()}
                    disabled={!brainDumpText || brainDumpMutation.isPending}
                  >
                    {brainDumpMutation.isPending ? 'Processing...' : 'Process with AI'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

